/**
 * O1 — Welcome / Brand Splash.
 *
 * 1:1 reproduction of assets/extracted/tier-1/O1-welcome.html.
 * Tap anywhere → /onboarding/1.
 */
import { t } from '../../i18n/runtime';
import type { RouteContext } from '../router';

/**
 * Build the golden-ratio equiangular spiral path used as Fudeto's brand mark.
 * Replicates the JS in the design template exactly so the stroke renders
 * identically.
 *
 *   r(θ) = r_end · φ^((θ_end − θ) / (π/2))
 *   - terminus pinned at (491.94, 512); θ_end = π; r_end = 20.06
 *   - winds ≈ 6.31 quarter-turns (≈ 1.58 full revolutions)
 */
function spiralD(): string {
  const cx = 512, cy = 512;
  const phi = 1.618033988749895;
  const tEnd = Math.PI;
  const rEnd = 20.06;
  const qTurns = 6.31;
  const tStart = tEnd - (qTurns * Math.PI) / 2;
  const steps = 900;
  let d = '';
  for (let i = 0; i <= steps; i++) {
    const t2 = tStart + ((tEnd - tStart) * i) / steps;
    const r = rEnd * Math.pow(phi, (tEnd - t2) / (Math.PI / 2));
    const x = cx + r * Math.cos(t2);
    const y = cy + r * Math.sin(t2);
    d += (i === 0 ? 'M ' : ' L ') + x.toFixed(2) + ' ' + y.toFixed(2);
  }
  return d;
}

const PATH_D = spiralD();

export default function (ctx: RouteContext): HTMLElement {
  const root = document.createElement('section');
  root.className = 'screen-welcome';
  root.setAttribute('role', 'button');
  root.tabIndex = 0;
  root.setAttribute('aria-label', t('a11y.welcome'));

  root.innerHTML = `
    <div class="splash">
      <svg class="logo" viewBox="0 0 1024 1024" aria-hidden="true">
        <path class="spiral-path" d="${PATH_D}" fill="none" stroke="currentColor" stroke-width="120" stroke-linecap="round" stroke-linejoin="round" pathLength="2400"></path>
        <circle class="spiral-dot" cx="491.94" cy="512" r="50" fill="#EAB308"></circle>
      </svg>
      <div class="wordmark">${t('brand.name')}</div>
      <div class="subtitle-1">${t('onboarding.welcome.subtitle')}</div>
      <div class="subtitle-2">${t('brand.kanji_subtitle')}</div>
    </div>
    <div class="cta">${t('onboarding.welcome.cta')}</div>
  `;

  const advance = () => ctx.navigate('/onboarding/1');
  root.addEventListener('click', advance);
  root.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      // Replay animation by re-mounting class.
      root.classList.remove('replay');
      void root.offsetWidth;
      root.classList.add('replay');
    }
  });
  return root;
}
